import axios from 'axios';

// This will be replaced by the actual API endpoint when deployed
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Helper to determine if we're in development mode or if the API isn't available
const isDev = process.env.NODE_ENV === 'development';
let apiAvailable = true;

// Auth token management
const AUTH_TOKEN_KEY = 'workout-auth-token';

// Get stored auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

// Set auth token in localStorage
const setAuthToken = (token) => {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
};

// Clear auth token from localStorage
const clearAuthToken = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
};

// Check if user is authenticated
const isAuthenticated = () => {
  return !!getAuthToken();
};

// Helper functions for API calls
export const api = {
  /**
   * Get workout history
   * @param {string} userId - User ID (optional)
   * @returns {Promise<Array>} - List of past workouts
   */
  /**
   * Login with password
   * @param {string} password - Password to authenticate with
   * @returns {Promise<boolean>} - Whether login was successful
   */
  login: async (password) => {
    if (!password) return false;
    
    try {
      console.log('Attempting login with token');
      
      // Test the token by making a request first
      const response = await axios.get(`${API_URL}/workouts?userId=default-user`, {
        headers: {
          'X-Auth-Token': password
        }
      });
      
      // If we get here, authentication was successful
      console.log('Authentication successful');
      apiAvailable = true;
      
      // Only store the token after successful auth
      setAuthToken(password);
      
      // Return success - don't force reload
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      // If authentication failed, clear the token
      if (error.response && error.response.status === 401) {
        clearAuthToken();
      }
      apiAvailable = false;
      return false;
    }
  },
  
  /**
   * Logout user
   */
  logout: () => {
    clearAuthToken();
  },
  
  /**
   * Check if user is authenticated
   * @returns {boolean} - Whether user is authenticated
   */
  isAuthenticated,
  
  getWorkouts: async (userId = 'default-user') => {
    if (!apiAvailable && isDev) {
      return []; // Return empty array in dev if API is not available
    }
    
    try {
      const authToken = getAuthToken();
      const headers = authToken ? { 'X-Auth-Token': authToken } : {};
      
      const response = await axios.get(`${API_URL}/workouts?userId=${userId}`, { headers });
      apiAvailable = true;
      return response.data;
    } catch (error) {
      console.error('Error fetching workouts:', error);
      apiAvailable = false;
      return []; // Return empty array on error
    }
  },

  /**
   * Record a completed workout
   * @param {Object} workout - Workout data with exercises
   * @returns {Promise<Object>} - Confirmation with ID
   */
  recordWorkout: async (workout) => {
    if (!apiAvailable && isDev) {
      console.log('API not available, storing in localStorage for now:', workout);
      try {
        // Fallback to localStorage in development if API is not available
        const savedWorkouts = JSON.parse(localStorage.getItem('workouts') || '[]');
        const newWorkout = { ...workout, id: Date.now() };
        savedWorkouts.push(newWorkout);
        localStorage.setItem('workouts', JSON.stringify(savedWorkouts));
        return { message: 'Workout saved locally', id: newWorkout.id };
      } catch (e) {
        console.error('Error saving to localStorage:', e);
      }
      return { message: 'API not available, workout not saved' };
    }
    
    try {
      const authToken = getAuthToken();
      const headers = authToken ? { 'X-Auth-Token': authToken } : {};
      
      const response = await axios.post(`${API_URL}/workouts`, workout, { headers });
      apiAvailable = true;
      return response.data;
    } catch (error) {
      console.error('Error recording workout:', error);
      apiAvailable = false;
      
      // Fallback to localStorage
      try {
        const savedWorkouts = JSON.parse(localStorage.getItem('workouts') || '[]');
        const newWorkout = { ...workout, id: Date.now() };
        savedWorkouts.push(newWorkout);
        localStorage.setItem('workouts', JSON.stringify(savedWorkouts));
        return { message: 'Workout saved locally', id: newWorkout.id };
      } catch (e) {
        console.error('Error saving to localStorage:', e);
      }
      
      throw error;
    }
  },

  /**
   * Get history for a specific exercise
   * @param {string} exerciseName - Name of the exercise
   * @param {string} userId - User ID (optional)
   * @returns {Promise<Array>} - Exercise history with weights and dates
   */
  getExerciseHistory: async (exerciseName, userId = 'default-user') => {
    if (!apiAvailable && isDev) {
      // In development, try to get data from localStorage if API is not available
      try {
        const savedWorkouts = JSON.parse(localStorage.getItem('workouts') || '[]');
        const exerciseHistory = [];
        
        savedWorkouts.forEach(workout => {
          const exerciseData = workout.exercises.find(ex => ex.name === exerciseName);
          if (exerciseData) {
            exerciseHistory.push({
              date: workout.date,
              weight: exerciseData.weight,
              reps: exerciseData.reps,
              sets: exerciseData.sets,
              wasSuccessful: exerciseData.wasSuccessful,
              workoutName: workout.workoutName
            });
          }
        });
        
        return exerciseHistory;
      } catch (e) {
        console.error('Error reading from localStorage:', e);
        return [];
      }
    }
    
    try {
      const authToken = getAuthToken();
      const headers = authToken ? { 'X-Auth-Token': authToken } : {};
      
      const response = await axios.get(
        `${API_URL}/exercises/${encodeURIComponent(exerciseName)}/history?userId=${userId}`,
        { headers }
      );
      apiAvailable = true;
      return response.data;
    } catch (error) {
      console.error(`Error fetching history for ${exerciseName}:`, error);
      apiAvailable = false;
      
      // Fallback to localStorage
      try {
        const savedWorkouts = JSON.parse(localStorage.getItem('workouts') || '[]');
        const exerciseHistory = [];
        
        savedWorkouts.forEach(workout => {
          const exerciseData = workout.exercises.find(ex => ex.name === exerciseName);
          if (exerciseData) {
            exerciseHistory.push({
              date: workout.date,
              weight: exerciseData.weight,
              reps: exerciseData.reps,
              sets: exerciseData.sets,
              wasSuccessful: exerciseData.wasSuccessful,
              workoutName: workout.workoutName
            });
          }
        });
        
        return exerciseHistory;
      } catch (e) {
        console.error('Error reading from localStorage:', e);
        return [];
      }
    }
  }
};